import { orgSelectors } from 'ducks/orgs'
import useQuoteReviewSuppliers from 'pages/ordering/hooks/useQuoteReviewSuppliers'
import { BetaChip } from 'elements/chip/BetaChip'
import {
  DEFAULT_ALLOW_ACCOUNT_MANAGER_ORDER,
  QUOTE_SOURCE_SUPPLIER_ASSIST_SHOP,
  QUOTE_STATUS_DRAFT,
  QUOTE_VALID_UNTIL_DEFAULT_DAYS,
  SUPPLIER_ASSIST_QUOTE_BASE_PATH,
} from 'hardware/constants'
import type { QuoteProjectDataType, QuoteType } from 'hardware/types'
import { EmailIcon, GroupAddIcon, MenuItem, Select, styled, Tooltip, Typography } from 'opensolar-ui'
import {
  SectionCardBottom,
  SectionCardTop,
  SectionHeaderTitle,
  TitleContainer,
} from 'pages/ordering/checkoutv3/preOrder/styles'
import { formatPricePerWattWithUnit, getPricePerWatt } from 'pages/ordering/detail/content/price/PriceDetail'
import { supplierPartNumberFromOrdering } from 'pages/supplierAssist/quote/utils'
import { getOutputPower } from 'pages/ordering/utils'
import { StyledRequestReviewQuoteButton, StyledRequestReviewQuoteButtonLabel } from 'pages/supplierAssist/quote/styles'
import QuoteReviewWidgetSupplierOption from 'projectSections/sections/hardware/bom/QuoteReviewWidgetSupplierOption'
import { useTranslate, useTranslateParse } from 'ra-core'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import type { RootState } from 'types/state'
import { currencySymbolForCountry, dateParserYYYYMMDD } from 'util/misc'

const QuoteReviewWidgetContainer = styled('div')({
  width: '100%',
  marginBottom: 24,
})

const StyledSupplierSelector = styled('div')({
  marginBottom: 16,
})
const SupplierImgContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px',
})

const SupplierImg = styled('img')(({ theme }) => ({
  width: 20,
  height: 20,
  objectFit: 'contain',
  border: `1px solid ${theme.palette.neutralGrey[500]}`,
  borderRadius: 4,
  flexShrink: 0,
}))

const ButtonSubtext = styled('div', { name: 'ButtonSubtext' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  margin: '10px 15px',
})

/**
 * Supplier Assist widget for the Cart Page. Uses orderable line items and org context.
 * Same UI as project BOM QuoteReviewWidget; builds quote from global cart (no project).
 */
export const CartPageSupplierAssistWidget = ({
  systemIds,
  orderableLineItems,
  projectData,
}: {
  systemIds?: number[]
  orderableLineItems: any[]
  projectData: QuoteProjectDataType[]
}) => {
  const translate = useTranslate()
  const translateParse = useTranslateParse()
  const history = useHistory()
  const location = useLocation()
  const { filteredSuppliers, defaultSupplierFilterKey } = useQuoteReviewSuppliers()
  const org = useSelector(orgSelectors.getOrg)
  const orgIso2 = useSelector(orgSelectors.getOrgIso2)
  const currencySymbol = useSelector((state: RootState) => currencySymbolForCountry(orgSelectors.getOrgIso2(state)))
  const lineItemsForSupplierAssist = useMemo(
    () => orderableLineItems.filter((li) => li && li.status !== 'loading'),
    [orderableLineItems]
  )

  const [selectedSupplier, setSelectedSupplier] = useState<string>(defaultSupplierFilterKey)

  useEffect(() => {
    const supplierKeys = filteredSuppliers.map((supplier) => supplier.filterKey)
    if (supplierKeys.length === 0) return
    if (!selectedSupplier || !supplierKeys.includes(selectedSupplier)) {
      setSelectedSupplier(defaultSupplierFilterKey)
    }
  }, [selectedSupplier, filteredSuppliers, defaultSupplierFilterKey])

  const accountManagerUrl = useMemo(
    () => filteredSuppliers?.find((s) => s?.filterKey === selectedSupplier)?.accountManager?.url,
    [filteredSuppliers, selectedSupplier]
  )
  const handleClickStartQuoteReviewRef = useRef<() => void>(() => {})

  const handleClickStartQuoteReview = () => {
    const countryIso2 = orgIso2 || org?.country?.iso2 || ''
    const systems = systemIds?.map((id) => `${window.API_ROOT}/api/orgs/${org?.id}/systems/${id}/`)
    const payload: QuoteType = {
      systems,
      account_managers: accountManagerUrl ? [accountManagerUrl] : [],
      supplier_filter_key: selectedSupplier,
      status: QUOTE_STATUS_DRAFT,
      data: {},
      notes: '',
      allow_account_manager_order: DEFAULT_ALLOW_ACCOUNT_MANAGER_ORDER,
      custom_data: {},
      notify_team_members: [],
      files_data: [],
      files_link: [],
      lock_pricing: false,
      address: (org as any)?.address ?? '',
      zip: (org as any)?.zip ?? '',
      state: (org as any)?.state ?? '',
      locality: (org as any)?.locality ?? '',
      country: (org as any)?.country?.name ?? '',
      country_iso2: countryIso2,
      created_date: new Date(),
      estimated_delivery_date: null,
      last_status: '',
      quote_valid_until: (() => {
        const d = new Date()
        d.setDate(d.getDate() + QUOTE_VALID_UNTIL_DEFAULT_DAYS)
        return dateParserYYYYMMDD(d) ?? undefined
      })(),
      project_data: projectData,
      source: QUOTE_SOURCE_SUPPLIER_ASSIST_SHOP,
    }

    const data: QuoteType['data'] = {}
    lineItemsForSupplierAssist.forEach((lineItem, index) => {
      const selectedSupplierOrderingData = lineItem.orderingData?.find((s) => s?.distributor === selectedSupplier)
      const pricePerUnit = selectedSupplierOrderingData?.price ?? 0
      const supplierProductName = selectedSupplierOrderingData?.short_description ?? lineItem.data?.short_description
      const componentTitle = lineItem?.componentTitle
      const productName = supplierProductName || componentTitle || ''
      const imageUrl = selectedSupplierOrderingData?.image_url
      const pricePerWatt = getPricePerWatt({
        power: getOutputPower(lineItem.rating || 0),
        price: pricePerUnit,
        quantityPerItem: lineItem.quantityPerItem || 1,
      })
      const hasPricePerWatt = ['module', 'inverter', 'battery'].includes(lineItem.componentType || '')
      data[index] = {
        code: lineItem.code,
        componentType: lineItem.componentType || 'other',
        componentTypeV2: lineItem.componentTypeV2 || lineItem.componentType || 'other',
        quantity: lineItem.quantity,
        source: selectedSupplier,
        price: pricePerUnit,
        product_name: productName || '',
        image_url: imageUrl,
        price_per_watt: hasPricePerWatt
          ? formatPricePerWattWithUnit(pricePerWatt || 0, currencySymbol, countryIso2)
          : undefined,
        sku: supplierPartNumberFromOrdering(selectedSupplierOrderingData, selectedSupplier),
        variantId: selectedSupplierOrderingData?.distributor_variant_id || '',
      }
    })
    payload.data = data

    history.push({
      pathname: SUPPLIER_ASSIST_QUOTE_BASE_PATH,
      state: { quote: payload, from: `${location.pathname}` },
    })
  }

  const startButtonLabel = useMemo(() => {
    const supplierConfig = filteredSuppliers?.find((supplier) => supplier?.filterKey === selectedSupplier)
    return translate('Share with %{supplier}', {
      supplier: supplierConfig?.accountManager?.display || supplierConfig?.name,
    })
  }, [filteredSuppliers, selectedSupplier])

  handleClickStartQuoteReviewRef.current = handleClickStartQuoteReview

  const requestReviewButton = useMemo(
    () => (
      <Tooltip title={startButtonLabel}>
        <span style={{ display: 'block', width: '100%' }}>
          <StyledRequestReviewQuoteButton
            variant="contained"
            color="secondary"
            onClick={() => handleClickStartQuoteReviewRef.current()}
            startIcon={<EmailIcon size={20} strokeWidth="1" />}
            disabled={lineItemsForSupplierAssist.length === 0}
          >
            <StyledRequestReviewQuoteButtonLabel>{startButtonLabel}</StyledRequestReviewQuoteButtonLabel>
          </StyledRequestReviewQuoteButton>
        </span>
      </Tooltip>
    ),
    [handleClickStartQuoteReviewRef, startButtonLabel, lineItemsForSupplierAssist.length]
  )

  if (filteredSuppliers.length === 0) return null

  return (
    <QuoteReviewWidgetContainer>
      <SectionCardTop>
        <TitleContainer>
          <GroupAddIcon size={24} />
          <SectionHeaderTitle>{translate('Supplier Assist')}</SectionHeaderTitle>
          <BetaChip />
        </TitleContainer>
      </SectionCardTop>
      <SectionCardBottom>
        <StyledSupplierSelector>
          <Select
            value={selectedSupplier}
            variant="outlined"
            onChange={(e) => setSelectedSupplier(e.target.value as string)}
            disabled={lineItemsForSupplierAssist.length === 0}
          >
            {filteredSuppliers.map((supplier) => (
              <MenuItem key={supplier?.filterKey} value={supplier?.filterKey}>
                <SupplierImgContainer>
                  <SupplierImg
                    src={supplier?.logoUrl || `${window.PUBLIC_URL}/images/component-image-placeholder.png`}
                  />
                  <QuoteReviewWidgetSupplierOption supplier={supplier} />
                </SupplierImgContainer>
              </MenuItem>
            ))}
          </Select>
        </StyledSupplierSelector>
        {requestReviewButton}
        <ButtonSubtext>
          <Typography variant="body3">
            {translateParse('Invite supplier to <a>review<a>, <b>finalise<b> and <c>quote<c> on your order.', {
              a: (label) => <strong>{label}</strong>,
              b: (label) => <strong>{label}</strong>,
              c: (label) => <strong>{label}</strong>,
            })}
          </Typography>
        </ButtonSubtext>
      </SectionCardBottom>
    </QuoteReviewWidgetContainer>
  )
}

export default CartPageSupplierAssistWidget
